import React, { useState } from "react";
import { Link } from 'react-router-dom';
import * as BosIdentityServices from "data/BosWebServices/BosIdentityServices";
import LoginHeader from "./LoginHeader";
import { getTranslation as translate } from "logic/LocaleManager";
import GoogleLogin from "../GoogleLogin";
import MicrosoftLogin from "../MicrosoftLogin";
import { googleClientId, googleProviderId, microsoftClientId, microsoftProviderId } from "../../GlobalConstants";
import { useCookiePreference } from "../../hooks/useCookiePreference";
import { useUserNotice } from "../../hooks/useUserNotice";
import CookieConsent from "../CookieConsent";
//import * as AppConfig from "logic/GlobalAppConfig";
//import * as ApiWebServices from "data/InternalWebServices/ApiWebServices";

import { LOGINTYPE_LOGIN, LOGINTYPE_REGISTER_NEWUSER, LOGINTYPE_REQUEST_EMAIL, LOGINTYPE_REGISTER_GLOBALUSER, LOGINTYPE_RESET_PASSWORD } from "pages/Login/LoginPage";

// DevExtreme components
import TextBox from 'devextreme-react/text-box';
import ValidationGroup from 'devextreme-react/validation-group';
import Validator, { RequiredRule } from 'devextreme-react/validator';
import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';

// Redux
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as bosUserActions from "../../redux/actions/bosUserActions";

// Component styles
import classes from "./Login.module.scss";

function Login(props) {
  const { changeLoginType, actionSuccessfull, actionError, cleanMessages, setIsLoading, isLoading, targetUri } = props;
  const loginFailedText = translate("loginAccount.failed", "Login failed!");
  const cookiesEnabled = navigator.cookieEnabled;

  const [allowRememberLogin, setAllowRememberLogin] = useCookiePreference("COOKIEPREF_ALLOWREMEMBERLOGIN");
  const [allowSocialLogin, setAllowSocialLogin] = useCookiePreference("COOKIEPREF_ALLOWSOCIALLOGIN");
  const { show: showCookieConsentNotice, RenderUserNotice: RenderCookieConsentNotice } = useUserNotice(cookieConsentNoticeClosed, true);

  //const [showIntegratedSecurity, setShowIntegratedSecurity] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showLoginOptions, setShowLoginOptions] = useState(false);

  const loginOptions = [
    {
      suggestion: translate("login.optionForgotQ", "Forgot password?"),
      loginType: LOGINTYPE_RESET_PASSWORD,
      text: translate("login.optionForgotA", "Reset password")
    },
    {
      suggestion: translate("login.optionNewQ", "New?"),
      loginType: LOGINTYPE_REGISTER_NEWUSER,
      text: translate("login.optionNewA", "Sign in")
    },
    {
      suggestion: translate("login.optionNoPasswordQ", "No password yet?"),
      loginType: LOGINTYPE_REQUEST_EMAIL,
      text: translate("login.optionNoPasswordA", "Request mail")
    },
    {
      suggestion: translate("login.optionExistingQ", "Existing BOS Account?"),
      loginType: LOGINTYPE_REGISTER_GLOBALUSER,
      text: translate("login.optionExistingA", "Sign in")
    }
  ];

  // useEffect(() => {
  //   let showIS = AppConfig.getUseIntegratedSecurity();
  //   setShowIntegratedSecurity(showIS);

  // }, []);

  const loginChanged = e => {
    setUsername(e.value);
    cleanMessages("");
  };

  const passwordChanged = e => {
    setPassword(e.value);
    cleanMessages("");
  };

  const rememberMeChanged = e => {
    if (e.value === true && !allowRememberLogin)
      showCookieConsentNotice();
    else
      setRememberMe(e.value);
  };

  function cookieConsentNoticeClosed(result) {
    setAllowRememberLogin(result.allowRememberLogin);
    setAllowSocialLogin(result.allowSocialLogin);
    setRememberMe(result.allowRememberLogin);
  }

  async function login(args) {
    args.preventDefault();
    cleanMessages("");
    await loginInternal(username, encodeValue(password));
  }

  async function loginWithProvider(type, token) {
    cleanMessages("");
    await loginExternal(type, token);
  }

  function loginWithProviderError(error) {
    props.bosUserActions.clearBosUser();
    console.error(loginFailedText + " (" + error + ")");
    actionError(loginFailedText);
    setIsLoading(false);
  }

  // async function loginIntegrated(event) {
  //   let userSID = await ApiWebServices.fetchIntegratedSecurityUserSID();
  //   await loginInternal(userSID, "-");
  // }

  async function loginInternal(usr, pwd) {
    try {
      setIsLoading(true);
      let result = await BosIdentityServices.fetchAuthenticate(usr, pwd, targetUri);
      if (!result || !result.token) throw new Error("NO TOKEN RECEIVED");
      props.bosUserActions.setBosUser({
        newDisplayName: result.userDisplayName,
        newAuthToken: result.token,
        newPersistIn: rememberMe ? "local" : "session"
      });
      actionSuccessfull(LOGINTYPE_LOGIN, result.startUri, null, result.enableLaunchpad);
    } catch (error) {
      props.bosUserActions.clearBosUser();
      console.error(loginFailedText + " (" + error + ")");
      actionError(loginFailedText);
      setIsLoading(false);
    }
  }

  async function loginExternal(providerId, token) {
    try {
      setIsLoading(true);
      let result = await BosIdentityServices.fetchAuthenticateExternal(providerId, token, targetUri);
      if (!result || !result.token) throw new Error("NO TOKEN RECEIVED");
      props.bosUserActions.setBosUser({
        newDisplayName: result.userDisplayName,
        newAuthToken: result.token,
        newPersistIn: rememberMe ? "local" : "session"
      });
      actionSuccessfull(LOGINTYPE_LOGIN, result.startUri, null, result.enableLaunchpad);
    } catch (error) {
      props.bosUserActions.clearBosUser();
      console.error(loginFailedText + " (" + error + ")");
      actionError(loginFailedText);
      setIsLoading(false);
    }
  }

  const encodeValue = value => {
    return btoa(value);
  }

  return (
    <form onSubmit={login}>
      <ValidationGroup>
        <LoginHeader title={translate("loginAccount.title", "Login")} description={translate("loginAccount.description", "Sign In to your account")} />
        <div className={'dx-field'}>
          <TextBox
            value={username}
            onValueChanged={loginChanged}
            placeholder={translate("loginAccount.textBoxEmailLabel", "Email")}
            disabled={isLoading}
            width={'100%'}
          >
            <Validator>
              <RequiredRule message={translate("loginAccount.textBoxEmailError", "Email is required")} />
            </Validator>
          </TextBox>
        </div>
        <div className={'dx-field'}>
          <TextBox
            mode={'password'}
            value={password}
            onValueChanged={passwordChanged}
            placeholder={translate("loginAccount.textBoxPasswordLabel", "Password")}
            disabled={isLoading}
            width={'100%'}
          >
            <Validator>
              <RequiredRule message={translate("loginAccount.textBoxPasswordError", "Password is required")} />
            </Validator>
          </TextBox>
        </div>
        <div className={'dx-field'}>
          <CheckBox
            value={rememberMe}
            text={translate("login.rememberMe", "Remember me")}
            onValueChanged={rememberMeChanged}
            disabled={isLoading}
          />
        </div>
        <div className={'dx-field'}>
          <Button
            type={'default'}
            text={translate("loginAccount.action", "Login")}
            useSubmitBehavior={true}
            disabled={isLoading}
            width={'100%'}
          />
        </div>
        <div className={'dx-field'}>
          <Link
            to=""
            onClick={(e) => {
              e.preventDefault();
              setShowLoginOptions(prev => !prev);
            }}>
            {translate("login.linkTrouble", "Trouble Signing in?")}
          </Link>
        </div>
        {showLoginOptions
          ? (
            loginOptions.map((o, index) => (
              <div key={index}>
                {o.suggestion}
                &nbsp;
                <Link
                  to=""
                  onClick={(e) => {
                    e.preventDefault();
                    changeLoginType(o.loginType);
                  }}>
                  {o.text}
                </Link>
              </div>
            ))
          )
          : null
        }
      </ValidationGroup>

      <div className={classes.loginSeparation}>
        <div><b>oder</b></div>
      </div>

      {cookiesEnabled && allowSocialLogin
        ? (
          <div className={classes.altLoginProviders}>
            <div className={classes.altLoginContent}>
              <GoogleLogin clientId={googleClientId} onSuccess={(token) => loginWithProvider(googleProviderId, token)} onFailure={(args) => loginWithProviderError(args)} />
            </div>
            <div className={classes.altLoginContent}>
              <MicrosoftLogin clientId={microsoftClientId} onSuccess={(token) => loginWithProvider(microsoftProviderId, token)} onFailure={(args) => loginWithProviderError(args)} />
            </div>
          </div>
        )
        : (
          <Link
          className={classes.alternateLoginOptions}
            to=""
            onClick={(e) => {
              e.preventDefault();
              showCookieConsentNotice();
            }}>
            {translate("login.showSocialLogins", "Alternative Anmeldeoptionen anzeigen")}
          </Link>
        )
      }

      {/* Dialogs */}
      <RenderCookieConsentNotice>
        <CookieConsent />
      </RenderCookieConsentNotice>
    </form>
  );
}

function mapDispatchToProps(dispatch) {
  return {
    bosUserActions: bindActionCreators(bosUserActions, dispatch)
  };
}

export default connect(
  null,
  mapDispatchToProps
)(Login);
